import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../utils/api";
import { API_CALL_STATUS } from "../../assets/constants/constants";
import { signOut as userSignOut } from "../user/userSlice";

api.defaults.withCredentials = true;

const initialState = {
  loggedInUser: null,

  // Separate statuses for stability
  authStatus: API_CALL_STATUS.IDLE,           // login, signup, google login
  sessionStatus: API_CALL_STATUS.IDLE,        // getLoggedInUser
  otpStatus: API_CALL_STATUS.IDLE,            // email OTP & verification
  forgotStatus: API_CALL_STATUS.IDLE,         // forgot-password request
  resetStatus: API_CALL_STATUS.IDLE,          // reset-password

  error: null,
  verifyEmailRes: null,
  otpVerificationRes: null,
  forgotPasswordRes: null,
};

// -------------------- THUNKS --------------------

export const signUp = createAsyncThunk(
  "auth/signUp",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post("/auth/signup", userData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

export const signIn = createAsyncThunk(
  "auth/signIn",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post("/auth/signin", userData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

export const google = createAsyncThunk(
  "auth/google",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post("/auth/google", userData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

export const getLoggedInUser = createAsyncThunk(
  "auth/get-logged-in-user",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/auth/get-logged-in-user");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

export const sendEmailToVerifyEmail = createAsyncThunk(
  "auth/sendEmailOtp",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post("/auth/send-otp", userData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

export const verifyOtpToVefifyEmail = createAsyncThunk(
  "auth/verifyEmailOtp",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post("/auth/verify-otp", userData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (userData, { rejectWithValue }) => {
    try {
      const res = await api.post("/auth/forget-password", userData);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ newPassword, confirmPassword, resetPasswordToken }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/auth/reset-password/${resetPasswordToken}`, {
        newPassword,
        confirmPassword,
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

// -------------------- SLICE --------------------

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder
      // ---------------- AUTH (SignIn/SignUp/Google) ----------------
      .addCase(signUp.pending, (state) => {
        state.authStatus = API_CALL_STATUS.LOADING;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.authStatus = API_CALL_STATUS.SUCCEEDED;
        state.loggedInUser = action.payload;
        state.error = null;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.authStatus = API_CALL_STATUS.FAILED;
        state.error = action.payload;
      })

      .addCase(signIn.pending, (state) => {
        state.authStatus = API_CALL_STATUS.LOADING;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.authStatus = API_CALL_STATUS.SUCCEEDED;
        state.loggedInUser = action.payload;
        state.error = null;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.authStatus = API_CALL_STATUS.FAILED;
        state.error = action.payload;
      })

      .addCase(google.pending, (state) => {
        state.authStatus = API_CALL_STATUS.LOADING;
      })
      .addCase(google.fulfilled, (state, action) => {
        state.authStatus = API_CALL_STATUS.SUCCEEDED;
        state.loggedInUser = action.payload;
        state.error = null;
      })
      .addCase(google.rejected, (state, action) => {
        state.authStatus = API_CALL_STATUS.FAILED;
        state.error = action.payload;
      })

      // ---------------- SESSION (getLoggedInUser) ----------------
      .addCase(getLoggedInUser.pending, (state) => {
        state.sessionStatus = API_CALL_STATUS.LOADING;
      })
      .addCase(getLoggedInUser.fulfilled, (state, action) => {
        state.sessionStatus = API_CALL_STATUS.SUCCEEDED;
        state.loggedInUser = action.payload;
      })
      .addCase(getLoggedInUser.rejected, (state, action) => {
        state.sessionStatus = API_CALL_STATUS.FAILED;
        state.error = action.payload;
      })

      // ---------------- EMAIL OTP ----------------
      .addCase(sendEmailToVerifyEmail.pending, (state) => {
        state.otpStatus = API_CALL_STATUS.LOADING;
      })
      .addCase(sendEmailToVerifyEmail.fulfilled, (state, action) => {
        state.otpStatus = API_CALL_STATUS.SUCCEEDED;
        state.verifyEmailRes = action.payload;
      })
      .addCase(sendEmailToVerifyEmail.rejected, (state, action) => {
        state.otpStatus = API_CALL_STATUS.FAILED;
        state.error = action.payload;
      })

      .addCase(verifyOtpToVefifyEmail.pending, (state) => {
        state.otpStatus = API_CALL_STATUS.LOADING;
      })
      .addCase(verifyOtpToVefifyEmail.fulfilled, (state, action) => {
        state.otpStatus = API_CALL_STATUS.SUCCEEDED;
        state.otpVerificationRes = action.payload;
      })
      .addCase(verifyOtpToVefifyEmail.rejected, (state, action) => {
        state.otpStatus = API_CALL_STATUS.FAILED;
        state.error = action.payload;
      })

      // ---------------- FORGOT PASSWORD ----------------
      .addCase(forgotPassword.pending, (state) => {
        state.forgotStatus = API_CALL_STATUS.LOADING;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.forgotStatus = API_CALL_STATUS.SUCCEEDED;
        state.forgotPasswordRes = action.payload;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.forgotStatus = API_CALL_STATUS.FAILED;
        state.error = action.payload;
      })

      // ---------------- RESET PASSWORD ----------------
      .addCase(resetPassword.pending, (state) => {
        state.resetStatus = API_CALL_STATUS.LOADING;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.resetStatus = API_CALL_STATUS.SUCCEEDED;
        state.forgotPasswordRes = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetStatus = API_CALL_STATUS.FAILED;
        state.error = action.payload;
        state.forgotPasswordRes = null;
      })

      // ---------------- LOGOUT (from userSlice) ----------------
      .addCase(userSignOut.fulfilled, (state) => {
        state.loggedInUser = null;

        // Reset only relevant statuses
        state.authStatus = API_CALL_STATUS.IDLE;
        state.sessionStatus = API_CALL_STATUS.IDLE;
        state.otpStatus = API_CALL_STATUS.IDLE;
        state.forgotStatus = API_CALL_STATUS.IDLE;
        state.resetStatus = API_CALL_STATUS.IDLE;

        // Clear OTP/Email/forgot/reset data
        state.verifyEmailRes = null;
        state.otpVerificationRes = null;
        state.forgotPasswordRes = null;
      });
  },
});

export default authSlice.reducer;
